/* eslint-disable ember/no-runloop, warp-drive/no-legacy-request-patterns */
import { isEmpty } from '@ember/utils';
import { next } from '@ember/runloop';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import config from 'linced-app/config/environment';
import moment from 'moment/moment';
import { hash } from '@linc-technologies/linced-common/utils/promise';

export function remapSummariesByLearner(allSummaries) {
  return Object.keys(allSummaries).reduce((fullMap, subjectId) => {
    let summaries = allSummaries[subjectId];
    if (isEmpty(summaries)) {
      return fullMap;
    }
    return summaries.reduce((mapped, summary) => {
      if (isEmpty(mapped[summary.personId])) {
        mapped[summary.personId] = {};
      }
      mapped[summary.personId][subjectId] = summary;
      return mapped;
    }, fullMap);
  }, {});
}

export function mapGradesByLearner(grades) {
  return grades.reduce((mapped, grade) => {
    if (isEmpty(mapped[grade.get('person.id')])) {
      mapped[grade.get('person.id')] = [];
    }
    mapped[grade.get('person.id')].push(grade);
    return mapped;
  }, {});
}

export function remapProgressionsByLearner(progressions) {
  return Object.keys(progressions).reduce((fullMap, subjectId) => {
    let subjectData = progressions[subjectId];
    if (isEmpty(subjectData)) {
      return fullMap;
    }
    return subjectData.reduce((mapped, progression) => {
      let personId = progression.get('person.id');
      if (isEmpty(mapped[personId])) {
        mapped[personId] = {};
      }
      if (isEmpty(mapped[personId][subjectId])) {
        mapped[personId][subjectId] = [];
      }
      mapped[personId][subjectId].push(progression);
      return mapped;
    }, fullMap);
  }, {});
}

export function getDataCardPromises(route, opts = {}) {
  let promises = {
    grades: [],
    subjectSummaries: {},
    subjectProgressions: {},
    attendanceSummaries: {},
    attendanceDays: {},
  };
  if (isEmpty(route) || isEmpty(opts)) {
    return promises;
  }

  // Load saved options from the personOptions service.
  const userOptions = route.personOptions.getAllDataCardOptions(
    route.session.person,
    ['assessmentIds', 'assessmentModuleIds', 'subjectIds', 'progressIds'],
  );

  // Grab the group from the (grand)parent route.
  let group = route.modelFor('group');

  if (
    !!opts.grades &&
    (!isEmpty(userOptions.assessmentIds) ||
      !isEmpty(userOptions.assessmentModuleIds))
  ) {
    promises.grades = route.store.query('assessments/grade', {
      groupId: group.id,
      assessmentIds: JSON.stringify(userOptions.assessmentIds || []),
      assessmentModuleIds: JSON.stringify(
        userOptions.assessmentModuleIds || [],
      ),
    });
  }

  if (!!opts.summaries && !isEmpty(userOptions.subjectIds)) {
    let summaryPromises = userOptions.subjectIds.reduce(
      (promises, subjectId) => {
        promises[subjectId] = route.heroApi.goals.listLearnerSummaries(
          group.get('school.id'),
          group.id,
          null,
          subjectId,
        );
        return promises;
      },
      {},
    );
    promises.subjectSummaries = hash(summaryPromises);
  }

  if (!!opts.progressions && !isEmpty(userOptions.progressIds)) {
    let progressionPromises = userOptions.progressIds.reduce(
      (promises, subjectId) => {
        promises[subjectId] = route.store.query('milestones/progression', {
          goalSubjectId: subjectId,
          type: ProgressionType.PROGRESSION,
          groupId: group.id,
          latest: true,
        });
        return promises;
      },
      {},
    );
    promises.subjectProgressions = hash(progressionPromises);
  }

  if (opts.attendance) {
    const rolloverDate = route.get('dates.rolloverDate');
    if (route.jurisdiction === Jurisdiction.NZ_MOE) {
      // NZ MoE schools use the group summary (learner summaries for a whole
      // group, mapped by person ID for easy retrieval).
      const params = new URLSearchParams({
        jurisdiction: group.get('school.jurisdiction'),
        schoolId: group.get('school.id'),
        groupId: group.id,
        startTime: moment(rolloverDate).unix(),
      });
      let attendanceSummaryUrl = `${config.APP.apiHost}/attendance/v4/groupsummary/nzmoe?${params}`;
      promises.attendanceSummaries = route.fetch
        .request(attendanceSummaryUrl)
        .then((resp) => resp.summaries);
    } else {
      // Non-NZ-MoE schools use the NzMoeDay records, which we'll map by person
      // ID for easy retrieval (since they come as a flat array).
      promises.attendanceDays = route.store
        .query('attendance/nz-moe-day', {
          schoolId: group.get('school.id'),
          groupId: group.id,
          dateFrom: rolloverDate,
          dateTo: moment().format('YYYY-MM-DD'),
        })
        .then((dayCodes) => {
          return dayCodes.reduce((byLearner, dayCode) => {
            if (isEmpty(byLearner[dayCode.person_id])) {
              byLearner[dayCode.person_id] = [];
            }
            byLearner[dayCode.person_id].push(dayCode);
            return byLearner;
          }, {});
        });
    }
  }

  return promises;
}

export function loadSavedPreferences(controller, model, options) {
  // Wait a moment to ensure model is fully processed and
  // controller computed properties are initialized
  next(() => {
    // Load saved options from the personOptions service
    const userOptions = controller.personOptions.getAllDataCardOptions(
      controller.session.person,
      options,
    );

    if (options.includes('assessmentIds')) {
      _loadSavedAssessments(controller, model, userOptions.assessmentIds || []);
    }
    if (options.includes('assessmentModuleIds')) {
      _loadSavedModules(
        controller,
        model,
        userOptions.assessmentModuleIds || [],
      );
    }

    // The progressSubjects and goalSubjects are computed properties on the controller
    // So we need to wait an additional tick for them to be available
    next(() => {
      if (options.includes('progressIds')) {
        _loadSavedProgressSubjects(controller, userOptions.progressIds || []);
      }
      if (options.includes('subjectIds')) {
        _loadSavedGoalSubjects(controller, userOptions.subjectIds || []);
      }
    });
  });
}

// Load saved assessments by ID
function _loadSavedAssessments(controller, model, savedAssessmentIds) {
  if (!model.assessments) return;

  const savedAssessments = model.assessments.filter((assessment) =>
    savedAssessmentIds.includes(assessment.id),
  );

  controller.selectedAssessments = savedAssessments;
}

// Load saved modules by ID
function _loadSavedModules(controller, model, savedModuleIds) {
  if (!model.modules) return;

  // First check directly in the modules collection
  const savedModules = model.modules.filter((module) =>
    savedModuleIds.includes(module.id),
  );

  controller.selectedModules = savedModules;
}

// Load saved progress subjects by ID
function _loadSavedProgressSubjects(controller, savedProgressIds) {
  const progressSubjects = controller.progressSubjects;
  if (!progressSubjects) return;

  const savedProgress = progressSubjects.filter((subject) =>
    savedProgressIds.includes(subject.id),
  );

  controller.selectedProgressSubjects = savedProgress;
}

// Load saved goal subjects by ID
function _loadSavedGoalSubjects(controller, savedSubjectIds) {
  const goalSubjects = controller.goalSubjects;
  if (!goalSubjects) return;

  const savedSubjects = goalSubjects.filter((subject) =>
    savedSubjectIds.includes(subject.id),
  );

  controller.selectedGoalSubjects = savedSubjects;
}
